import { useState, type FC, type MouseEvent, type ReactNode } from 'react';
import { Button, ConfigProvider, Divider, Dropdown, Space, Tag, Typography } from 'antd';
import { TbArrowUpRight, TbFileInfo, TbFileText } from 'react-icons/tb';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import DropdownPanel from '@/shared/components/dropdowns/DropdownPanel';
import { dayjs } from '@/utilities';

import type { FinancialSourceAttribution } from './types';
import { hasFinancialFilingLocator } from './utils';

interface FinancialSourceAttributionPanelProps {
    attributions: FinancialSourceAttribution[];
    onOpenFiling: (attribution: FinancialSourceAttribution) => void;
    onRequestClose?: () => void;
}

interface FinancialSourceAttributionButtonProps extends FinancialSourceAttributionPanelProps {
    children?: ReactNode;
}

const displayDate = (value?: string) => {
    if (!value || !dayjs(value).isValid()) {
        return undefined;
    }

    return dayjs(value).format('MMM D, YYYY');
};

const MetadataItem: FC<{ label: string; value?: string }> = ({ label, value }) => {
    const { token } = useAntDesignTheme();

    if (!value) {
        return null;
    }

    return (
        <div style={{ display: 'flex', gap: '1rem', justifyContent: 'space-between' }}>
            <Typography.Text style={{ color: token.colorTextTertiary, fontSize: token.fontSizeSM }}>
                {label}
            </Typography.Text>
            <Typography.Text style={{ fontSize: token.fontSizeSM, textAlign: 'right' }}>{value}</Typography.Text>
        </div>
    );
};

const SourceCard: FC<{
    attribution: FinancialSourceAttribution;
    onOpenFiling: (attribution: FinancialSourceAttribution) => void;
    onRequestClose?: () => void;
}> = ({ attribution, onOpenFiling, onRequestClose }) => {
    const { token } = useAntDesignTheme();
    const filingDate = displayDate(attribution.filingDate);
    const acceptedDate = displayDate(attribution.acceptedDate);

    const handleOpenFiling = (event: MouseEvent<HTMLElement>) => {
        event.stopPropagation();
        onRequestClose?.();
        onOpenFiling(attribution);
    };

    return (
        <div
            style={{
                alignItems: 'center',
                background: token.colorFillQuaternary,
                border: `1px solid ${token.colorBorderSecondary}`,
                borderRadius: token.borderRadiusSM,
                display: 'flex',
                gap: '0.625rem',
                minWidth: 0,
                padding: '0.5rem',
            }}
        >
            <span
                style={{
                    alignItems: 'center',
                    background: token.colorBgElevated,
                    border: `1px solid ${token.colorBorderSecondary}`,
                    borderRadius: token.borderRadiusSM,
                    color: token.colorTextTertiary,
                    display: 'inline-flex',
                    flex: '0 0 auto',
                    height: '1.75rem',
                    justifyContent: 'center',
                    width: '1.75rem',
                }}
            >
                <TbFileText size='1rem' />
            </span>
            <div style={{ display: 'flex', flex: 1, flexDirection: 'column', minWidth: 0 }}>
                <Typography.Text strong ellipsis style={{ fontSize: token.fontSizeSM }}>
                    {attribution.filingType || 'SEC Filing'}
                    {attribution.period ? ` · ${attribution.period}` : ''}
                </Typography.Text>
                <Typography.Text ellipsis style={{ color: token.colorTextTertiary, fontSize: token.fontSizeSM }}>
                    {[
                        filingDate ? `Filed ${filingDate}` : undefined,
                        acceptedDate ? `Accepted ${acceptedDate}` : undefined,
                    ]
                        .filter(Boolean)
                        .join(' · ') || 'Source filing metadata'}
                </Typography.Text>
            </div>
            <Button
                aria-label={`View ${attribution.filingType || 'SEC filing'}`}
                disabled={!hasFinancialFilingLocator(attribution)}
                icon={<TbArrowUpRight size='1rem' />}
                onClick={handleOpenFiling}
                size='small'
                type='text'
            >
                View
            </Button>
        </div>
    );
};

export const FinancialSourceAttributionPanel: FC<FinancialSourceAttributionPanelProps> = ({
    attributions,
    onOpenFiling,
    onRequestClose,
}) => {
    const { token } = useAntDesignTheme();
    const primaryAttribution = attributions[0];
    const hasMetricContext = attributions.length === 1 && Boolean(primaryAttribution?.lineItemLabel);

    return (
        <ConfigProvider componentSize='small'>
            {/* viewport-layout-allow: source attribution is a floating menu constrained to the browser edge. */}
            <DropdownPanel width='22rem' maxHeight='min(26rem, 70vh)'>
                <div style={{ padding: '0.75rem' }}>
                    <Space direction='vertical' size='small' style={{ width: '100%' }}>
                        <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'space-between', minWidth: 0 }}>
                            <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                                <Typography.Text strong ellipsis>
                                    {hasMetricContext
                                        ? primaryAttribution?.lineItemLabel
                                        : `${attributions.length} Financial Source${attributions.length === 1 ? '' : 's'}`}
                                </Typography.Text>
                                <Typography.Text
                                    ellipsis
                                    style={{ color: token.colorTextTertiary, fontSize: token.fontSizeSM }}
                                >
                                    {hasMetricContext
                                        ? 'Reported financial statement value'
                                        : 'Source filings for the selected period columns'}
                                </Typography.Text>
                            </div>
                            {hasMetricContext && primaryAttribution?.formattedValue ? (
                                <Typography.Text strong style={{ flex: '0 0 auto' }}>
                                    {primaryAttribution.formattedValue}
                                </Typography.Text>
                            ) : null}
                        </div>

                        {hasMetricContext ? (
                            <>
                                <Space size={4} wrap>
                                    {primaryAttribution?.period ? <Tag>{primaryAttribution.period}</Tag> : null}
                                    {primaryAttribution?.filingType ? <Tag>{primaryAttribution.filingType}</Tag> : null}
                                    {primaryAttribution?.currency ? <Tag>{primaryAttribution.currency}</Tag> : null}
                                </Space>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                                    <MetadataItem label='Symbol' value={primaryAttribution?.symbol} />
                                    <MetadataItem label='Filed' value={displayDate(primaryAttribution?.filingDate)} />
                                    <MetadataItem
                                        label='Accepted'
                                        value={displayDate(primaryAttribution?.acceptedDate)}
                                    />
                                </div>
                            </>
                        ) : null}
                    </Space>
                </div>
                <Divider style={{ margin: 0 }} />
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', padding: '0.75rem' }}>
                    <Typography.Text style={{ color: token.colorTextTertiary, fontSize: token.fontSizeSM }}>
                        Source attribution
                    </Typography.Text>
                    {attributions.map((attribution, index) => (
                        <SourceCard
                            attribution={attribution}
                            key={`${attribution.period ?? 'source'}-${attribution.filingUrl ?? index}`}
                            onOpenFiling={onOpenFiling}
                            onRequestClose={onRequestClose}
                        />
                    ))}
                </div>
            </DropdownPanel>
        </ConfigProvider>
    );
};

export const FinancialSourceAttributionButton: FC<FinancialSourceAttributionButtonProps> = ({
    attributions,
    children,
    onOpenFiling,
}) => {
    const [open, setOpen] = useState(false);

    if (!attributions.length) {
        return null;
    }

    return (
        <Dropdown
            popupRender={() => (
                <FinancialSourceAttributionPanel
                    attributions={attributions}
                    onOpenFiling={onOpenFiling}
                    onRequestClose={() => setOpen(false)}
                />
            )}
            onOpenChange={setOpen}
            open={open}
            placement='top'
            trigger={['click']}
        >
            {children ?? (
                <Button
                    aria-label='View financial source metadata'
                    icon={<TbFileInfo size='1rem' />}
                    size='small'
                    type='dashed'
                >
                    {attributions.length === 1 ? 'Source' : `${attributions.length} Sources`}
                </Button>
            )}
        </Dropdown>
    );
};
